import React, { useMemo } from 'react';
import { scaleLinear } from 'd3-scale';

import { toHash } from '@mono/to-hash';
import { WXTilesV3Layer } from '@mono/types/src/platmap';

import { stateToStyle } from './stateToStyle';
import { ColorMapLegend } from '@metservicedev/met-ui/wxtiles';

interface WXTilesV3LegendProps {
    wxtilesV3State: WXTilesV3Layer;
    displayValue?: number;
    outerWidth?: number;
}

export const WXTilesV3Legend = ({ wxtilesV3State, displayValue, outerWidth }: WXTilesV3LegendProps) => {
    const wxStyle = stateToStyle(wxtilesV3State);
    if (wxtilesV3State.styleOverrides.colorMap) {
        const colorMap = wxtilesV3State.legendColorMap ?? wxtilesV3State.styleOverrides.colorMap;

        return (
            <ColorMapLegend
                colorMap={colorMap}
                units={wxtilesV3State.unitLabel && wxtilesV3State.unitLabel.length !== 0 ? wxtilesV3State.unitLabel : wxtilesV3State.styleOverrides.units}
                displayValue={displayValue}
                evenlyLabelMarks={wxtilesV3State.evenlyLabelMarks}
                markTitles={wxtilesV3State.markTitles}
                marks={wxtilesV3State.marks}
                outerContainerWidth={outerWidth}
            />
        );
    }
    if (!wxStyle.levels) return null;
    if (!wxStyle.colors) return null;

    const min = wxStyle.levels[0];
    const max = wxStyle.levels[wxStyle.levels.length - 1];

    const colourLengthToValue = scaleLinear()
        .range([min, max])
        .domain([0, wxStyle.colors.length - 1]);

    const datumToColour = scaleLinear()
        //@ts-ignore range can be string[], d3 is compatible with hex string colours
        .range(wxStyle.colors)
        .domain(wxStyle.colors.map((_, i) => colourLengthToValue(i)));

    const colorMap: [number, string][] | undefined = useMemo(() => {
        // This style is set when the legend is non-linear
        if (wxtilesV3State.styleOverrides.colorMap) return wxtilesV3State.styleOverrides.colorMap;

        if (!wxStyle.colors) return;

        // From here onwards the legend is linear, and a colormap can be generated with just the min, max, and colours
        return Array.from({ length: wxStyle.colors.length }).map((_, i) => {
            const value = colourLengthToValue(i);
            const colour = datumToColour(value);
            if (!colour) return [value, 'rgba(0, 0, 0, 0)'];
            return [value, colour.toString()] as [number, string];
        });
    }, [toHash(wxtilesV3State)]);

    const marks = useMemo(() => {
        const levels = wxtilesV3State.levels;
        if (levels === undefined) return;
        const interval = levels.interval;
        if (interval === undefined || interval === null || interval === 0) return [levels.min, levels.max];

        return Array.from({ length: (levels.max - levels.min) / interval + 1 }, (_, i) => {
            return levels.min + i * interval;
        });
    }, [toHash(wxtilesV3State)]);

    if (!colorMap) return <></>;
    const validMarks = wxtilesV3State.customMarks?.map((v) => parseFloat(v)).filter((v) => !isNaN(v));

    return (
        <ColorMapLegend
            colorMap={colorMap}
            units={wxtilesV3State.unitLabel && wxtilesV3State.unitLabel.length !== 0 ? wxtilesV3State.unitLabel : wxtilesV3State.styleOverrides.units}
            marks={validMarks && validMarks.length > 0 ? validMarks : marks}
            displayValue={displayValue}
            evenlyLabelMarks={wxtilesV3State.evenlyLabelMarks}
            outerContainerWidth={outerWidth}
        />
    );
};
